import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { apiUrl, apiUrlDev } from "../../globalVariables";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  ComposedChart,
} from "recharts";
import {
  FaUsers,
  FaCrown,
  FaUserPlus,
  FaUserTimes,
  FaGift,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaPercentage,
  FaSearch,
  FaSortAmountDown,
  FaSortAmountUp,
  FaUserSlash,
  FaCalendarAlt,
  FaFilter,
  FaChartBar,
  FaChartPie,
  FaFire,
  FaTimes,
  FaExchangeAlt,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";

/* ─── helpers ──────────────────────────────────────────── */
const fmtDate = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};
const fmtDateTime = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const timeSince = (ts) => {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NON_SUMMARY_MESSAGES = new Set([
  "No summary available.",
  "No transcript found for this meeting.",
  "No Fireflies transcript found for this meeting.",
]);

/* ─── Calendar week bounds ─────────────────────────────── */
// weekOffset: 0 = current week (Mon→now), -1 = last full week (Mon→Sun)
function getCalWeek(weekOffset = 0) {
  const now = new Date();
  const day = now.getDay(); // 0=Sun,1=Mon…6=Sat
  const daysFromMon = day === 0 ? 6 : day - 1;
  const mon = new Date(now);
  mon.setHours(0, 0, 0, 0);
  mon.setDate(now.getDate() - daysFromMon + weekOffset * 7);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  const endDt = weekOffset === 0 ? now : sun;
  return {
    start: mon.getTime(),
    end: weekOffset === 0 ? Date.now() : sun.getTime(),
    label: `${mon.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${endDt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
  };
}

/* ─── Week dropdown options (last N weeks) ──────────────── */
function getWeekOptions(count = 8) {
  return Array.from({ length: count }, (_, i) => {
    const bounds = getCalWeek(-i);
    const label =
      i === 0
        ? `This Week (${bounds.label})`
        : i === 1
          ? `Last Week (${bounds.label})`
          : `${i} Weeks Ago (${bounds.label})`;
    return { offset: -i, label };
  });
}
const WEEK_OPTIONS = getWeekOptions(8);

/* ─── Per-card week-comparison metadata ─────────────────── */
// dateField: which timestamp to use for "this week" bucketing
// goodDir:  +1 = increase is good (green), -1 = increase is bad (red)
const CARD_WEEK_META = {
  demos: { dateField: "demoDate", goodDir: +1 },
  converted: { dateField: "dateSignedUp", goodDir: +1 },
  freeSignup: { dateField: "demoDate", goodDir: +1 }, // more in-funnel = good
  noSignup: { dateField: "demoDate", goodDir: -1 }, // more dropoffs = bad
  paidNoDemo: { dateField: "dateSignedUp", goodDir: +1 },
  freeNoDemo: { dateField: "dateSignedUp", goodDir: +1 },
};

/* ─── date range presets ────────────────────────────────── */
const RANGE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "60d", days: 60 },
  { label: "90d", days: 90 },
  { label: "All Time", days: null },
];

/* ─── card configs ─────────────────────────────────────── */
const CARDS = [
  {
    key: "demos",
    label: "Total Demos",
    description: "All demo calls in selected period",
    category: null, // special: union of converted + freeSignup + noSignup
    icon: FaUsers,
    gradient: "#3b82f6",
    columns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "company", label: "Company" },
      { key: "demoDate", label: "Demo Date", render: fmtDate },
      { key: "source", label: "Source" },
      { key: "phone", label: "Phone" },
      { key: "website", label: "Website" },
      { key: "howTheyHeard", label: "How They Heard" },
    ],
  },
  {
    key: "converted",
    label: "Converted (Paid)",
    description: "Had demo → signed up → paying",
    category: "converted",
    icon: FaCrown,
    gradient: "#16a34a",
    columns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "company", label: "Company" },
      { key: "demoDate", label: "Demo Date", render: fmtDate },
      { key: "paidPlan", label: "Plan" },
      { key: "dateSignedUp", label: "Signed Up", render: fmtDate },
      { key: "source", label: "Source" },
    ],
  },
  {
    key: "freeSignup",
    label: "Free Signups",
    description: "Had demo → signed up → free plan",
    category: "freeSignup",
    icon: FaUserPlus,
    gradient: "#7c3aed",
    columns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "company", label: "Company" },
      { key: "demoDate", label: "Demo Date", render: fmtDate },
      { key: "dateSignedUp", label: "Signed Up", render: fmtDate },
      { key: "source", label: "Source" },
    ],
  },
  {
    key: "noSignup",
    label: "Demo → No Signup",
    description: "Had demo but never created an account",
    category: "noSignup",
    icon: FaUserTimes,
    gradient: "#ea580c",
    columns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "company", label: "Company" },
      { key: "demoDate", label: "Demo Date", render: fmtDate },
      { key: "source", label: "Source" },
      { key: "phone", label: "Phone" },
      { key: "website", label: "Website" },
      { key: "howTheyHeard", label: "How They Heard" },
    ],
  },
  {
    key: "paidNoDemo",
    label: "Paid Without Demo",
    description: "Paying users who never booked a demo",
    category: "paidNoDemo",
    icon: FaGift,
    gradient: "#0891b2",
    columns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "paidPlan", label: "Plan" },
      { key: "dateSignedUp", label: "Signed Up", render: fmtDate },
      { key: "source", label: "Source" },
    ],
  },
  {
    key: "freeNoDemo",
    label: "Free, No Demo",
    description: "Signed up free but never booked a demo",
    category: "freeNoDemo",
    icon: FaUserSlash,
    gradient: "#475569",
    columns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "dateSignedUp", label: "Signed Up", render: fmtDate },
      { key: "source", label: "Source" },
    ],
  },
];

/* ─── Filter allUsers by date range ───────────────────── */
function filterUsersByDateRange(allUsers, startTs, endTs) {
  if (!startTs && !endTs) return allUsers;
  return allUsers.filter((u) => {
    const isDemo =
      u.category === "noSignup" ||
      u.category === "converted" ||
      u.category === "freeSignup";
    const pivot = isDemo ? u.demoDate : u.dateSignedUp;
    if (!pivot) return false;
    if (startTs && pivot < startTs) return false;
    if (endTs && pivot > endTs) return false;
    return true;
  });
}

/* ─── Granularity helper ────────────────────────────────── */
// Returns "daily" | "weekly" | "biweekly" | "monthly"
function getGranularity(preset, startTs) {
  if (preset === "7d") return "daily";
  if (preset === "30d") return "weekly";
  if (preset === "60d") return "biweekly";
  if (preset === "90d") return "monthly";
  // All Time or custom — auto-detect from startTs
  if (!startTs) return "monthly";
  const days = (Date.now() - startTs) / (24 * 60 * 60 * 1000);
  if (days <= 14) return "daily";
  if (days <= 60) return "weekly";
  if (days <= 120) return "biweekly";
  return "monthly";
}

/* ─── Build chart data with adaptive granularity ─────────── */
// granularity: "daily" | "weekly" | "biweekly" | "monthly"
const BIWEEKLY_ANCHOR = new Date("2024-01-01").getTime(); // stable 2-week epoch anchor

function getBucket(ts, granularity) {
  const d = new Date(ts);
  if (granularity === "daily") {
    d.setHours(0, 0, 0, 0);
    return {
      key: d.getTime(),
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
  }
  if (granularity === "weekly") {
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow; // roll back to Monday
    const mon = new Date(d);
    mon.setDate(d.getDate() + diff);
    mon.setHours(0, 0, 0, 0);
    return {
      key: mon.getTime(),
      label: mon.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }
  if (granularity === "biweekly") {
    // Snap to nearest 2-week boundary from anchor
    const MS2W = 14 * 24 * 60 * 60 * 1000;
    const slot = Math.floor((d.getTime() - BIWEEKLY_ANCHOR) / MS2W);
    const start = new Date(BIWEEKLY_ANCHOR + slot * MS2W);
    return {
      key: start.getTime(),
      label: start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }
  // monthly
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return {
    key: first.getTime(),
    label: first.toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    }),
  };
}

function buildChartData(allUsers, granularity) {
  const demoUsers = allUsers.filter(
    (u) =>
      ["converted", "freeSignup", "noSignup"].includes(u.category) &&
      u.demoDate,
  );
  if (!demoUsers.length) return [];

  const bucketMap = new Map();
  demoUsers.forEach((u) => {
    const { key, label } = getBucket(u.demoDate, granularity);
    if (!bucketMap.has(key)) {
      bucketMap.set(key, {
        key,
        label,
        total: 0,
        converted: 0,
        freeSignup: 0,
        noSignup: 0,
      });
    }
    const b = bucketMap.get(key);
    b.total += 1;
    b[u.category] = (b[u.category] || 0) + 1;
  });

  return Array.from(bucketMap.values()).sort((a, b) => a.key - b.key);
}

/* ─── Drill-down table with search + sort ──────────────── */
function DrillDownTable({ card, users }) {
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter((row) =>
      card.columns.some((col) => {
        const val = row[col.key];
        return val && String(val).toLowerCase().includes(q);
      }),
    );
  }, [users, search, card.columns]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortCol] ?? "";
      const bVal = b[sortCol] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number")
        return sortAsc ? aVal - bVal : bVal - aVal;
      return sortAsc
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filtered, sortCol, sortAsc]);

  const toggleSort = (colKey) => {
    if (sortCol === colKey) setSortAsc(!sortAsc);
    else {
      setSortCol(colKey);
      setSortAsc(true);
    }
  };

  if (!users.length) {
    return (
      <div className="text-muted text-center py-4" style={{ fontSize: 14 }}>
        No data in this period.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              fontSize: 14,
            }}
          />
          <input
            className="form-control"
            style={{ paddingLeft: 34, fontSize: 14 }}
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="text-muted" style={{ fontSize: 13 }}>
          {filtered.length} of {users.length} users
        </span>
      </div>
      <div
        className="table-responsive"
        style={{ maxHeight: 380, overflowY: "auto" }}
      >
        <table
          className="table table-sm table-hover align-middle mb-0"
          style={{ fontSize: 13 }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#f9fafb",
              zIndex: 1,
            }}
          >
            <tr>
              <th style={{ width: 40, padding: "8px 12px" }}>#</th>
              {card.columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => toggleSort(col.key)}
                >
                  <span className="d-flex align-items-center gap-1">
                    {col.label}
                    {sortCol === col.key &&
                      (sortAsc ? (
                        <FaSortAmountUp style={{ fontSize: 11 }} />
                      ) : (
                        <FaSortAmountDown style={{ fontSize: 11 }} />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, idx) => (
              <tr key={row.email || idx}>
                <td style={{ color: "#9ca3af", padding: "6px 12px" }}>
                  {idx + 1}
                </td>
                {card.columns.map((col) => {
                  const val = row[col.key];
                  const display = col.render ? col.render(val) : val || "-";
                  return (
                    <td key={col.key} style={{ padding: "6px 12px" }}>
                      {col.key === "source" && val ? (
                        <span
                          style={{
                            fontSize: 11,
                            background:
                              val === "Direct" ? "#f3f4f6" : "#ede9fe",
                            color: val === "Direct" ? "#6b7280" : "#7c3aed",
                            padding: "2px 8px",
                            borderRadius: 999,
                            fontWeight: 500,
                          }}
                        >
                          {display}
                        </span>
                      ) : col.key === "paidPlan" && val ? (
                        <span
                          style={{
                            fontSize: 11,
                            background: val === "FREE" ? "#f3f4f6" : "#d1fae5",
                            color: val === "FREE" ? "#6b7280" : "#065f46",
                            padding: "2px 8px",
                            borderRadius: 999,
                            fontWeight: 500,
                          }}
                        >
                          {display}
                        </span>
                      ) : (
                        display
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Chart color palette ───────────────────────────────── */
const C = {
  demo: "#3b82f6", // blue-500
  converted: "#16a34a", // green-700
  freeSignup: "#7c3aed", // violet-700
  noSignup: "#ea580c", // orange-600
  paidNoDemo: "#0891b2", // cyan-600
  freeNoDemo: "#475569", // slate-600
  signedUp: "#15803d", // green-800
};

/* ─── Charts Section ────────────────────────────────────── */
function ChartsSection({ users, granularity }) {
  const countByCategory = (cat) =>
    users.filter((u) => u.category === cat).length;
  const demoTotal =
    countByCategory("converted") +
    countByCategory("freeSignup") +
    countByCategory("noSignup");
  const convertedCount = countByCategory("converted");
  const freeSignupCount = countByCategory("freeSignup");
  const noSignupCount = countByCategory("noSignup");
  const paidNoDemoCount = countByCategory("paidNoDemo");
  const freeNoDemoCount = countByCategory("freeNoDemo");

  // Funnel steps (top → bottom, each narrower)
  const funnelSteps = [
    { label: "Total Demos", value: demoTotal, color: C.demo },
    {
      label: "Signed Up",
      value: convertedCount + freeSignupCount,
      color: C.signedUp,
    },
    { label: "Converted (Paid)", value: convertedCount, color: C.converted },
  ].filter((s) => s.value > 0);

  // Donut data
  const pieData = [
    { name: "Converted", value: convertedCount, color: C.converted },
    { name: "Free Signup", value: freeSignupCount, color: C.freeSignup },
    { name: "No Signup", value: noSignupCount, color: C.noSignup },
    { name: "Paid No Demo", value: paidNoDemoCount, color: C.paidNoDemo },
    { name: "Free No Demo", value: freeNoDemoCount, color: C.freeNoDemo },
  ].filter((d) => d.value > 0);
  const totalPie = pieData.reduce((a, d) => a + d.value, 0);

  const chartData = buildChartData(users, granularity);
  const maxFunnel = funnelSteps[0]?.value || 1;
  const granularityLabel =
    {
      daily: "Daily",
      weekly: "Weekly",
      biweekly: "Bi-weekly",
      monthly: "Monthly",
    }[granularity] || "Weekly";

  // Enrich each bucket with % change vs previous bucket.
  // For converted we compare conversion rate = converted / total demos.
  const chartDataWithPct = chartData.map((d, i) => {
    const conversionRate = d.total > 0 ? (d.converted / d.total) * 100 : null;
    if (i === 0) {
      return {
        ...d,
        conversionRate,
        pctChange: null,
        conversionRateChangePct: null,
        conversionRateDiffPp: null,
      };
    }
    const prev = chartData[i - 1];
    const prevTotal = prev.total;
    const prevRate =
      prev.total > 0 ? (prev.converted / prev.total) * 100 : null;
    return {
      ...d,
      conversionRate,
      pctChange: prevTotal
        ? Math.round(((d.total - prevTotal) / prevTotal) * 100)
        : null,
      conversionRateChangePct:
        prevRate && conversionRate !== null
          ? Math.round(((conversionRate - prevRate) / prevRate) * 100)
          : null,
      conversionRateDiffPp:
        prevRate !== null && conversionRate !== null
          ? Math.round((conversionRate - prevRate) * 100) / 100
          : null,
    };
  });

  // Overall trend: first bucket → last bucket
  const firstBucket = chartDataWithPct[0]?.total || 0;
  const lastBucket = chartDataWithPct[chartDataWithPct.length - 1]?.total || 0;
  const overallPct =
    firstBucket > 0
      ? Math.round(((lastBucket - firstBucket) / firstBucket) * 100)
      : null;
  const firstConvRate =
    firstBucket > 0
      ? ((chartDataWithPct[0]?.converted || 0) / firstBucket) * 100
      : null;
  const lastConvRate =
    lastBucket > 0
      ? ((chartDataWithPct[chartDataWithPct.length - 1]?.converted || 0) /
          lastBucket) *
        100
      : null;
  const overallConvRatePct = firstConvRate
    ? Math.round(((lastConvRate - firstConvRate) / firstConvRate) * 100)
    : null;
  const overallConvRateDiffPp =
    firstConvRate !== null && lastConvRate !== null
      ? Math.round((lastConvRate - firstConvRate) * 100) / 100
      : null;

  const card = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "20px 18px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
  };
  const titleRow = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
  };
  const iconBox = (bg) => ({
    width: 26,
    height: 26,
    borderRadius: 6,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    color: "#fff",
    background: bg,
  });

  return (
    <>
      {/* Row 1: Funnel (left) + Donut (right) */}
      <div className="row g-3 mb-3">
        {/* ── Conversion Funnel ─────────────────────────────── */}
        <div className="col-12 col-lg-6">
          <div style={card}>
            <div style={titleRow}>
              <span style={iconBox(C.demo)}>
                <FaChartBar />
              </span>
              Conversion Funnel
            </div>
            {funnelSteps.length === 0 ? (
              <div
                className="text-muted text-center py-3"
                style={{ fontSize: 13 }}
              >
                No data
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {funnelSteps.map((step, i) => {
                  const widthPct = Math.max((step.value / maxFunnel) * 100, 8);
                  const dropPct =
                    i > 0
                      ? Math.round((step.value / funnelSteps[0].value) * 100)
                      : null;
                  return (
                    <div key={i}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 4,
                          fontSize: 12,
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#374151" }}>
                          {step.label}
                        </span>
                        <span style={{ color: "#6b7280" }}>
                          <strong style={{ color: "#111827" }}>
                            {step.value}
                          </strong>
                          {dropPct !== null && (
                            <span style={{ marginLeft: 6, color: "#9ca3af" }}>
                              ({dropPct}%)
                            </span>
                          )}
                        </span>
                      </div>
                      <div
                        style={{
                          background: "#f3f4f6",
                          borderRadius: 6,
                          height: 30,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${widthPct}%`,
                            height: "100%",
                            background: step.color,
                            borderRadius: 6,
                            transition: "width 0.5s ease",
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: 10,
                          }}
                        >
                          {widthPct > 18 && (
                            <span
                              style={{
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {step.value}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Category Breakdown Donut ───────────────────────── */}
        <div className="col-12 col-lg-6">
          <div style={card}>
            <div style={titleRow}>
              <span style={iconBox(C.converted)}>
                <FaChartPie />
              </span>
              Category Breakdown
            </div>
            {pieData.length === 0 ? (
              <div
                className="text-muted text-center py-3"
                style={{ fontSize: 13 }}
              >
                No data
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* Donut */}
                <div style={{ flexShrink: 0 }}>
                  <ResponsiveContainer width={150} height={150}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                      >
                        {pieData.map((e, i) => (
                          <Cell key={i} fill={e.color} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, n) => [`${v} users`, n]}
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Custom legend */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {pieData.map((e, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", alignItems: "center", gap: 7 }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: e.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 12, color: "#374151", flex: 1 }}>
                        {e.name}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {e.value}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          minWidth: 34,
                          textAlign: "right",
                        }}
                      >
                        {totalPie > 0
                          ? `${Math.round((e.value / totalPie) * 100)}%`
                          : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Demos Over Time — full width */}
      <div className="row g-3 mb-0">
        <div className="col-12">
          <div style={card}>
            <div style={{ ...titleRow, justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={iconBox(C.demo)}>
                  <FaCalendarAlt />
                </span>
                Demos Over Time
                <span
                  style={{ fontSize: 11, fontWeight: 400, color: "#9ca3af" }}
                >
                  ({granularityLabel})
                </span>
              </div>
              {/* Overall trend badges */}
              {chartDataWithPct.length >= 2 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {overallPct !== null && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 10px",
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background:
                          overallPct > 0
                            ? "#f0fdf4"
                            : overallPct < 0
                              ? "#fef2f2"
                              : "#f3f4f6",
                        color:
                          overallPct > 0
                            ? "#15803d"
                            : overallPct < 0
                              ? "#b91c1c"
                              : "#6b7280",
                        border: `1px solid ${overallPct > 0 ? "#bbf7d0" : overallPct < 0 ? "#fecaca" : "#e5e7eb"}`,
                      }}
                    >
                      {overallPct > 0 ? "▲" : overallPct < 0 ? "▼" : "="}
                      {overallPct > 0 ? "+" : ""}
                      {overallPct}% Demos
                    </span>
                  )}
                  {overallConvRatePct !== null &&
                    overallConvRateDiffPp !== null && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 999,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background:
                            overallConvRateDiffPp > 0
                              ? "#f0fdf4"
                              : overallConvRateDiffPp < 0
                                ? "#fef2f2"
                                : "#f3f4f6",
                          color:
                            overallConvRateDiffPp > 0
                              ? "#15803d"
                              : overallConvRateDiffPp < 0
                                ? "#b91c1c"
                                : "#6b7280",
                          border: `1px solid ${overallConvRateDiffPp > 0 ? "#bbf7d0" : overallConvRateDiffPp < 0 ? "#fecaca" : "#e5e7eb"}`,
                        }}
                      >
                        {overallConvRateDiffPp > 0
                          ? "▲"
                          : overallConvRateDiffPp < 0
                            ? "▼"
                            : "="}
                        {overallConvRatePct > 0 ? "+" : ""}
                        {overallConvRatePct}% Conv Rate
                        <span style={{ opacity: 0.85 }}>
                          ({overallConvRateDiffPp > 0 ? "+" : ""}
                          {overallConvRateDiffPp}pp)
                        </span>
                      </span>
                    )}
                </div>
              )}
            </div>
            {chartDataWithPct.length === 0 ? (
              <div
                className="text-muted text-center py-3"
                style={{ fontSize: 13 }}
              >
                No data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart
                  data={chartDataWithPct}
                  margin={{ top: 24, right: 24, left: 0, bottom: 0 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f3f4f6"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const idx = chartDataWithPct.findIndex(
                        (d) => d.label === label,
                      );
                      const pct =
                        idx > 0 ? chartDataWithPct[idx]?.pctChange : null;
                      return (
                        <div
                          style={{
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "10px 14px",
                            fontSize: 12,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 700,
                              marginBottom: 7,
                              color: "#111827",
                            }}
                          >
                            {label}
                          </div>
                          {payload.map((p, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                                marginBottom: 3,
                              }}
                            >
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: p.color,
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ color: "#6b7280" }}>
                                {p.name}:
                              </span>
                              <strong style={{ color: "#111827" }}>
                                {p.value}
                              </strong>
                            </div>
                          ))}
                          {(pct !== null ||
                            (idx > 0 &&
                              chartDataWithPct[idx]?.conversionRateDiffPp !==
                                null)) && (
                            <div
                              style={{
                                marginTop: 8,
                                paddingTop: 7,
                                borderTop: "1px solid #f3f4f6",
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                              }}
                            >
                              {pct !== null && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color:
                                      pct > 0
                                        ? "#15803d"
                                        : pct < 0
                                          ? "#b91c1c"
                                          : "#9ca3af",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  {pct > 0 ? "▲" : pct < 0 ? "▼" : "="}
                                  {pct > 0 ? "+" : ""}
                                  {pct}% Total Demos vs prev
                                </div>
                              )}
                              {(() => {
                                const convRatePp =
                                  chartDataWithPct[idx]?.conversionRateDiffPp;
                                const convRatePct =
                                  chartDataWithPct[idx]
                                    ?.conversionRateChangePct;
                                if (
                                  convRatePp === null ||
                                  convRatePp === undefined
                                )
                                  return null;
                                return (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color:
                                        convRatePp > 0
                                          ? "#15803d"
                                          : convRatePp < 0
                                            ? "#b91c1c"
                                            : "#9ca3af",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    {convRatePp > 0
                                      ? "▲"
                                      : convRatePp < 0
                                        ? "▼"
                                        : "="}
                                    {convRatePp > 0 ? "+" : ""}
                                    {convRatePp}pp Conv Rate vs prev
                                    {convRatePct !== null &&
                                      convRatePct !== undefined && (
                                        <span style={{ opacity: 0.85 }}>
                                          ({convRatePct > 0 ? "+" : ""}
                                          {convRatePct}%)
                                        </span>
                                      )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="converted"
                    stackId="a"
                    fill={C.converted}
                    name="Converted"
                  />
                  <Bar
                    dataKey="freeSignup"
                    stackId="a"
                    fill={C.freeSignup}
                    name="Free Signup"
                  />
                  <Bar
                    dataKey="noSignup"
                    stackId="a"
                    fill={C.noSignup}
                    name="No Signup"
                    radius={[3, 3, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke={C.demo}
                    strokeWidth={2}
                    dot={{ r: 3, fill: C.demo, strokeWidth: 0 }}
                    name="Total Demos"
                    label={(props) => {
                      const { x, y, index } = props;
                      // Only render labels when not too many points (avoids clutter)
                      if (chartDataWithPct.length > 12) return null;
                      const pct = chartDataWithPct[index]?.pctChange;
                      if (pct === null || pct === undefined) return null;
                      const color =
                        pct > 0 ? "#15803d" : pct < 0 ? "#dc2626" : "#9ca3af";
                      return (
                        <text
                          key={index}
                          x={x}
                          y={y - 9}
                          textAnchor="middle"
                          fontSize={10}
                          fontWeight="700"
                          fill={color}
                        >
                          {pct > 0 ? "+" : ""}
                          {pct}%
                        </text>
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Warmth config ─────────────────────────────────────── */
const WARMTH_CFG = {
  Hot: {
    icon: "🔥",
    label: "Hot",
    accent: "#ef4444",
    pill: { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5" },
  },
  Warm: {
    icon: "🌤",
    label: "Warm",
    accent: "#f97316",
    pill: { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
  },
  Cold: {
    icon: "❄️",
    label: "Cold",
    accent: "#3b82f6",
    pill: { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" },
  },
  Unknown: {
    icon: "—",
    label: "Unknown",
    accent: "#9ca3af",
    pill: { bg: "#f9fafb", color: "#6b7280", border: "#d1d5db" },
  },
};

/* helper — first two initials from a name */
function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

function toWebsiteHref(website) {
  if (!website) return "";
  const raw = String(website).trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

/* ─── Potential Leads Modal ─────────────────────────────── */
function PotentialLeadsPanel({ onClose }) {
  const [warmthData, setWarmthData] = useState(null);
  const [warmthLoading, setWarmthLoading] = useState(true);
  const [warmthError, setWarmthError] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(null);
  const [copiedPhone, setCopiedPhone] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const year = new Date().getFullYear();

        // Load warmth analyses + lead-conversion year doc in parallel
        const [warmthSnap, convSnap] = await Promise.all([
          getDoc(doc(firestoreProd, "admin-metrics", `lead-warmth-${year}`)),
          getDoc(
            doc(firestoreProd, "admin-metrics", `lead-conversion-${year}`),
          ),
        ]);

        const analyses = warmthSnap.exists()
          ? warmthSnap.data().analyses || []
          : [];

        // Build a lookup map from the lead-conversion year doc (email -> details).
        // Keep newest row but backfill blank fields from duplicates.
        const detailsMap = {};
        const isFilled = (v) => {
          if (v === null || v === undefined) return false;
          if (typeof v === "string") return v.trim().length > 0;
          return true;
        };
        const DETAIL_KEYS = [
          "name",
          "company",
          "phone",
          "website",
          "howTheyHeard",
          "source",
          "bookedWith",
          "demoDate",
          "demoDateFormatted",
          "category",
        ];

        if (convSnap.exists()) {
          (convSnap.data().allUsers || []).forEach((u) => {
            const email = (u.email || "").toLowerCase().trim();
            if (!email) return;

            if (!detailsMap[email]) {
              detailsMap[email] = { ...u };
              return;
            }

            const existing = detailsMap[email];
            const existingTs = existing.demoDate || existing.dateSignedUp || 0;
            const newTs = u.demoDate || u.dateSignedUp || 0;
            const winner = newTs > existingTs ? { ...u } : { ...existing };
            const loser = newTs > existingTs ? existing : u;

            DETAIL_KEYS.forEach((field) => {
              if (!isFilled(winner[field]) && isFilled(loser[field])) {
                winner[field] = loser[field];
              }
            });

            detailsMap[email] = winner;
          });
        }

        // Merge details into each analysis entry (backfills existing stored data)
        const enriched = analyses.map((a) => ({
          ...a,
          name: a.name || detailsMap[a.email?.toLowerCase()]?.name || "",
          company:
            a.company || detailsMap[a.email?.toLowerCase()]?.company || "",
          phone: a.phone || detailsMap[a.email?.toLowerCase()]?.phone || "",
          website:
            a.website || detailsMap[a.email?.toLowerCase()]?.website || "",
          howTheyHeard:
            a.howTheyHeard ||
            detailsMap[a.email?.toLowerCase()]?.howTheyHeard ||
            "",
          source:
            a.source || detailsMap[a.email?.toLowerCase()]?.source || "Direct",
          bookedWith:
            a.bookedWith ||
            detailsMap[a.email?.toLowerCase()]?.bookedWith ||
            "",
          demoDate:
            a.demoDate || detailsMap[a.email?.toLowerCase()]?.demoDate || null,
          category:
            a.category ||
            detailsMap[a.email?.toLowerCase()]?.category ||
            "freeSignup",
        }));

        setWarmthData(enriched);
      } catch (e) {
        setWarmthError(e.message || "Failed to load warmth data");
      } finally {
        setWarmthLoading(false);
      }
    })();
  }, []);

  const WARMTH_ORDER = { Hot: 0, Warm: 1, Cold: 2, Unknown: 3 };

  const counts = useMemo(() => {
    if (!warmthData) return { All: 0, Hot: 0, Warm: 0, Cold: 0, Unknown: 0 };
    return ["All", "Hot", "Warm", "Cold", "Unknown"].reduce((acc, f) => {
      acc[f] =
        f === "All"
          ? warmthData.length
          : warmthData.filter((a) => a.warmth === f).length;
      return acc;
    }, {});
  }, [warmthData]);

  const filtered = useMemo(() => {
    if (!warmthData) return [];
    let rows =
      activeFilter === "All"
        ? [...warmthData]
        : warmthData.filter((a) => a.warmth === activeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.email || "").toLowerCase().includes(q) ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.company || "").toLowerCase().includes(q) ||
          (r.phone || "").toLowerCase().includes(q) ||
          (r.website || "").toLowerCase().includes(q) ||
          (r.howTheyHeard || "").toLowerCase().includes(q) ||
          (r.source || "").toLowerCase().includes(q) ||
          (r.bookedWith || "").toLowerCase().includes(q) ||
          (r.summary || "").toLowerCase().includes(q),
      );
    }
    return rows.sort(
      (a, b) => (WARMTH_ORDER[a.warmth] ?? 3) - (WARMTH_ORDER[b.warmth] ?? 3),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warmthData, activeFilter, search]);

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(email);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const copyPhone = (phone) => {
    navigator.clipboard.writeText(phone).then(() => {
      setCopiedPhone(phone);
      setTimeout(() => setCopiedPhone(null), 2000);
    });
  };

  /* sidebar filter items */
  const SIDEBAR_FILTERS = [
    { key: "All", label: "All Leads", icon: null, color: "#374151" },
    { key: "Hot", label: "Hot — Priority", icon: "🔥", color: "#ef4444" },
    { key: "Warm", label: "Warm — Interested", icon: "🌤", color: "#f97316" },
    { key: "Cold", label: "Cold — Nurture", icon: "❄️", color: "#3b82f6" },
    { key: "Unknown", label: "No Transcript", icon: "—", color: "#9ca3af" },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* ── Modal shell ─────────────────────────────────── */}
      <div
        style={{
          width: "100%",
          maxWidth: 1180,
          height: "90vh",
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 32px 80px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)",
          display: "flex",
          overflow: "hidden",
          animation: "popIn 0.2s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        {/* ── LEFT SIDEBAR ──────────────────────────────── */}
        <div
          style={{
            width: 260,
            flexShrink: 0,
            background: "#fafafa",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            padding: "28px 0",
          }}
        >
          {/* Logo + title */}
          <div
            style={{
              padding: "0 20px 24px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                marginBottom: 12,
                background: "linear-gradient(135deg,#ef4444,#f97316)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 14px rgba(239,68,68,0.35)",
              }}
            >
              <FaFire style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div
              style={{
                fontWeight: 800,
                fontSize: 17,
                color: "#111827",
                letterSpacing: "-0.3px",
              }}
            >
              Potential Leads
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
              AI-analyzed from Fireflies
            </div>
          </div>

          {/* Filter nav */}
          <div style={{ padding: "16px 12px", flex: 1 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                marginBottom: 8,
                paddingLeft: 8,
              }}
            >
              Filter by warmth
            </div>
            {SIDEBAR_FILTERS.map(({ key, label, icon, color }) => {
              const isActive = activeFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveFilter(key)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "9px 12px",
                    borderRadius: 10,
                    marginBottom: 2,
                    border: "none",
                    cursor: "pointer",
                    background: isActive ? "#fff" : "transparent",
                    boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                    transition: "all 0.12s",
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? color : "#4b5563",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {icon && <span>{icon}</span>}
                    {!icon && (
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          background: "#e5e7eb",
                          display: "inline-block",
                        }}
                      />
                    )}
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      background: isActive ? color : "#f3f4f6",
                      color: isActive ? "#fff" : "#6b7280",
                      padding: "1px 8px",
                      borderRadius: 999,
                      minWidth: 24,
                      textAlign: "center",
                    }}
                  >
                    {counts[key] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Close button at bottom */}
          <div style={{ padding: "0 12px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#6b7280",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                transition: "all 0.12s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fee2e2";
                e.currentTarget.style.borderColor = "#fca5a5";
                e.currentTarget.style.color = "#ef4444";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              <FaTimes style={{ fontSize: 12 }} /> Close
            </button>
          </div>
        </div>

        {/* ── RIGHT CONTENT ──────────────────────────────── */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Top bar */}
          <div
            style={{
              padding: "20px 28px",
              borderBottom: "1px solid #e5e7eb",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {/* Search */}
            <div style={{ position: "relative", flex: 1, maxWidth: 420 }}>
              <FaSearch
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9ca3af",
                  fontSize: 13,
                }}
              />
              <input
                style={{
                  width: "100%",
                  padding: "9px 14px 9px 36px",
                  borderRadius: 10,
                  border: "1.5px solid #e5e7eb",
                  fontSize: 13,
                  outline: "none",
                  background: "#f9fafb",
                  color: "#111827",
                  transition: "border 0.15s",
                }}
                placeholder="Search name, email, company, source, website, or AI summary..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ef4444";
                  e.target.style.background = "#fff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.background = "#f9fafb";
                }}
              />
            </div>
            <div
              style={{ fontSize: 13, color: "#9ca3af", whiteSpace: "nowrap" }}
            >
              {filtered.length} lead{filtered.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* ── Cards list ─────────────────────────────── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
            {/* Loading */}
            {warmthLoading && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "60%",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "3px solid #f3f4f6",
                    borderTopColor: "#ef4444",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <p style={{ color: "#9ca3af", fontSize: 14, margin: 0 }}>
                  Loading warmth analysis…
                </p>
              </div>
            )}

            {/* Error */}
            {!warmthLoading && warmthError && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: 12,
                  padding: "14px 18px",
                  color: "#b91c1c",
                  fontSize: 13,
                }}
              >
                {warmthError}
              </div>
            )}

            {/* Empty */}
            {!warmthLoading &&
              !warmthError &&
              warmthData &&
              warmthData.length === 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "60%",
                    gap: 10,
                  }}
                >
                  <FaFire style={{ fontSize: 40, color: "#e5e7eb" }} />
                  <p style={{ color: "#9ca3af", fontSize: 14, margin: 0 }}>
                    No warmth data yet — run a data refresh.
                  </p>
                </div>
              )}

            {/* No results */}
            {!warmthLoading &&
              !warmthError &&
              warmthData &&
              warmthData.length > 0 &&
              filtered.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: 14,
                    marginTop: 60,
                  }}
                >
                  No leads match that filter or search.
                </div>
              )}

            {/* Lead cards */}
            {!warmthLoading && !warmthError && filtered.length > 0 && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                {filtered.map((lead, idx) => {
                  const cfg = WARMTH_CFG[lead.warmth] || WARMTH_CFG.Unknown;
                  const pill = cfg.pill;
                  const hasTs =
                    lead.summary &&
                    lead.transcriptFound !== false &&
                    !NON_SUMMARY_MESSAGES.has(lead.summary);
                  const isCopied = copied === lead.email;
                  const isPhoneCopied = copiedPhone === lead.phone;
                  const initials = getInitials(lead.name);

                  return (
                    <div
                      key={lead.email || idx}
                      style={{
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderLeft: `4px solid ${cfg.accent}`,
                        borderRadius: 14,
                        padding: "20px 22px",
                        transition: "box-shadow 0.18s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(0,0,0,0.08)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.boxShadow =
                          "0 1px 3px rgba(0,0,0,0.05)")
                      }
                    >
                      {/* Row 1: avatar + name/email + badges */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 14,
                          marginBottom: 14,
                        }}
                      >
                        {/* Avatar */}
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            flexShrink: 0,
                            background: pill.bg,
                            border: `1.5px solid ${pill.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            fontWeight: 800,
                            color: pill.color,
                            letterSpacing: "-0.5px",
                          }}
                        >
                          {initials}
                        </div>

                        {/* Name + company + email */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 15,
                              color: "#111827",
                              marginBottom: 1,
                            }}
                          >
                            {lead.name || "Unknown"}
                            {lead.company && (
                              <span
                                style={{
                                  fontWeight: 400,
                                  fontSize: 13,
                                  color: "#6b7280",
                                  marginLeft: 8,
                                }}
                              >
                                · {lead.company}
                              </span>
                            )}
                          </div>
                          {/* Contact chips — email + phone */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                              marginTop: 5,
                            }}
                          >
                            {/* Email chip */}
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0,
                                border: `1px solid ${isCopied ? "#86efac" : "#dbeafe"}`,
                                borderRadius: 8,
                                overflow: "hidden",
                                background: isCopied ? "#f0fdf4" : "#eff6ff",
                                transition: "all 0.15s",
                              }}
                            >
                              <a
                                href={`mailto:${lead.email}`}
                                style={{
                                  fontSize: 12,
                                  color: isCopied ? "#16a34a" : "#2563eb",
                                  textDecoration: "none",
                                  padding: "4px 8px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                  fontWeight: 500,
                                }}
                              >
                                <span style={{ fontSize: 11 }}>✉️</span>
                                {lead.email}
                              </a>
                              <button
                                type="button"
                                onClick={() => copyEmail(lead.email)}
                                title="Copy email"
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: "4px 8px",
                                  background: "transparent",
                                  color: isCopied ? "#16a34a" : "#3b82f6",
                                  cursor: "pointer",
                                  border: "none",
                                  borderLeft: `1px solid ${isCopied ? "#86efac" : "#bfdbfe"}`,
                                  transition: "all 0.15s",
                                }}
                              >
                                {isCopied ? "✓" : "⎘"}
                              </button>
                            </div>

                            {/* Phone chip */}
                            {lead.phone && (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0,
                                  border: `1px solid ${isPhoneCopied ? "#86efac" : "#e5e7eb"}`,
                                  borderRadius: 8,
                                  overflow: "hidden",
                                  background: isPhoneCopied
                                    ? "#f0fdf4"
                                    : "#f9fafb",
                                  transition: "all 0.15s",
                                }}
                              >
                                <a
                                  href={`tel:${lead.phone}`}
                                  style={{
                                    fontSize: 12,
                                    color: isPhoneCopied
                                      ? "#16a34a"
                                      : "#374151",
                                    textDecoration: "none",
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    fontWeight: 500,
                                  }}
                                >
                                  <span style={{ fontSize: 11 }}>📞</span>
                                  {lead.phone}
                                </a>
                                <button
                                  type="button"
                                  onClick={() => copyPhone(lead.phone)}
                                  title="Copy phone"
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    padding: "4px 8px",
                                    background: "transparent",
                                    color: isPhoneCopied
                                      ? "#16a34a"
                                      : "#6b7280",
                                    cursor: "pointer",
                                    border: "none",
                                    borderLeft: `1px solid ${isPhoneCopied ? "#86efac" : "#e5e7eb"}`,
                                    transition: "all 0.15s",
                                  }}
                                >
                                  {isPhoneCopied ? "✓" : "⎘"}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Marketing context chips */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                              marginTop: 7,
                            }}
                          >
                            {lead.source && (
                              <span
                                title="Lead source"
                                style={{
                                  fontSize: 11.5,
                                  color: "#4f46e5",
                                  background: "#eef2ff",
                                  border: "1px solid #c7d2fe",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                }}
                              >
                                Source: {lead.source}
                              </span>
                            )}
                            {lead.howTheyHeard && (
                              <span
                                title="How they heard about Addy"
                                style={{
                                  fontSize: 11.5,
                                  color: "#7c2d12",
                                  background: "#fff7ed",
                                  border: "1px solid #fed7aa",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                }}
                              >
                                Heard via: {lead.howTheyHeard}
                              </span>
                            )}
                            {lead.bookedWith && (
                              <span
                                title="Demo booked with"
                                style={{
                                  fontSize: 11.5,
                                  color: "#065f46",
                                  background: "#ecfdf5",
                                  border: "1px solid #a7f3d0",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                }}
                              >
                                Booked with: {lead.bookedWith}
                              </span>
                            )}
                            {lead.website && (
                              <a
                                href={toWebsiteHref(lead.website)}
                                target="_blank"
                                rel="noreferrer"
                                title={lead.website}
                                style={{
                                  fontSize: 11.5,
                                  color: "#1d4ed8",
                                  background: "#eff6ff",
                                  border: "1px solid #bfdbfe",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  textDecoration: "none",
                                  maxWidth: 260,
                                  display: "inline-block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Website: {lead.website}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Right: warmth + category + date */}
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          <div style={{ marginBottom: 6 }}>
                            <span
                              style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "3px 10px",
                                borderRadius: 999,
                                background: pill.bg,
                                color: pill.color,
                                border: `1px solid ${pill.border}`,
                              }}
                            >
                              {cfg.icon} {cfg.label}
                            </span>
                          </div>
                          <div className="d-flex justify-content-end gap-1 flex-wrap">
                            <span
                              style={{
                                fontSize: 11,
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontWeight: 600,
                                background:
                                  lead.category === "noSignup"
                                    ? "#fff7ed"
                                    : "#f5f3ff",
                                color:
                                  lead.category === "noSignup"
                                    ? "#c2410c"
                                    : "#7c3aed",
                                border: `1px solid ${lead.category === "noSignup" ? "#fed7aa" : "#ddd6fe"}`,
                              }}
                            >
                              {lead.category === "noSignup"
                                ? "No Signup"
                                : "Free Plan"}
                            </span>
                            {lead.demoDate && (
                              <span style={{ fontSize: 11, color: "#9ca3af" }}>
                                {fmtDate(lead.demoDate)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Row 2: AI Summary */}
                      <div
                        style={{
                          background: hasTs ? "#f8faff" : "#fafafa",
                          border: `1px solid ${hasTs ? "#c7d7fe" : "#f3f4f6"}`,
                          borderRadius: 10,
                          padding: "12px 16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: 1.1,
                            textTransform: "uppercase",
                            marginBottom: 6,
                            color: hasTs ? "#4f46e5" : "#9ca3af",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              background: hasTs ? "#4f46e5" : "#d1d5db",
                              display: "inline-block",
                            }}
                          />
                          AI Summary
                        </div>
                        {hasTs ? (
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13,
                              color: "#374151",
                              lineHeight: 1.7,
                            }}
                          >
                            {lead.summary}
                          </p>
                        ) : (
                          <p
                            style={{
                              margin: 0,
                              fontSize: 12.5,
                              color: "#9ca3af",
                              fontStyle: "italic",
                            }}
                          >
                            {lead.transcriptFound === true
                              ? lead.summary ||
                                "Fireflies transcript found, but no AI summary is available yet."
                              : "No Fireflies transcript found for this meeting."}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.96) translateY(12px); opacity: 0; }
          to   { transform: scale(1)    translateY(0);    opacity: 1; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────── */
const LeadConversionPage = ({ onReady }) => {
  const [rawData, setRawData] = useState(null); // full cache doc from Firestore
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [expandedCard, setExpandedCard] = useState(null);
  const [showWarmthPanel, setShowWarmthPanel] = useState(false);

  // Date range state
  const [selectedPreset, setSelectedPreset] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [weekAOffset, setWeekAOffset] = useState(0); // default: this week
  const [weekBOffset, setWeekBOffset] = useState(-1); // default: last week

  // ── Compute active date range ────────────────────────────
  const { startTs, endTs } = useMemo(() => {
    if (selectedPreset === "custom") {
      return {
        startTs: customStart
          ? new Date(customStart).setHours(0, 0, 0, 0)
          : null,
        endTs: customEnd
          ? new Date(customEnd).setHours(23, 59, 59, 999)
          : Date.now(),
      };
    }
    const preset = RANGE_PRESETS.find((p) => p.label === selectedPreset);
    if (!preset || preset.days === null)
      return { startTs: null, endTs: Date.now() };
    return {
      startTs: Date.now() - preset.days * 24 * 60 * 60 * 1000,
      endTs: Date.now(),
    };
  }, [selectedPreset, customStart, customEnd]);

  // ── allUsersSource: normalized + deduped, NO date filter ──
  // Used by weekStats so week buckets work independently of the period picker
  const allUsersSource = useMemo(() => {
    if (!rawData) return [];

    // Normalize old field names (plan→paidPlan, signedUpDate→dateSignedUp)
    const normalize = (u) => ({
      ...u,
      paidPlan: u.paidPlan || u.plan || null,
      dateSignedUp: u.dateSignedUp || u.signedUpDate || null,
    });

    let source;
    if (rawData.allUsers && rawData.allUsers.length > 0) {
      source = rawData.allUsers.map(normalize);
    } else {
      source = [
        ...(rawData.convertedList || []).map((u) =>
          normalize({ ...u, category: "converted" }),
        ),
        ...(rawData.freeSignupsList || []).map((u) =>
          normalize({ ...u, category: "freeSignup" }),
        ),
        ...(rawData.noSignupList || []).map((u) =>
          normalize({ ...u, category: "noSignup" }),
        ),
        ...(rawData.paidWithoutDemoList || []).map((u) =>
          normalize({ ...u, category: "paidNoDemo" }),
        ),
        ...(rawData.freeWithoutDemoList || []).map((u) =>
          normalize({ ...u, category: "freeNoDemo" }),
        ),
      ];
    }

    // Deduplicate by email — keep the entry with the most recent demoDate (or dateSignedUp as fallback)
    const emailMap = new Map();
    for (const u of source) {
      const key = (u.email || "").toLowerCase().trim();
      if (!key) continue;
      if (!emailMap.has(key)) {
        emailMap.set(key, u);
      } else {
        const existing = emailMap.get(key);
        const existingTs = existing.demoDate || existing.dateSignedUp || 0;
        const newTs = u.demoDate || u.dateSignedUp || 0;
        if (newTs > existingTs) emailMap.set(key, u);
      }
    }
    return Array.from(emailMap.values());
  }, [rawData]);

  // ── allUsers: filtered by active date range ───────────────
  const allUsers = useMemo(
    () => filterUsersByDateRange(allUsersSource, startTs, endTs),
    [allUsersSource, startTs, endTs],
  );

  // ── Per-card user lists ──────────────────────────────────
  const usersByCategory = useMemo(() => {
    const map = {};
    for (const card of CARDS) {
      if (card.key === "demos") {
        map[card.key] = allUsers.filter((u) =>
          ["converted", "freeSignup", "noSignup"].includes(u.category),
        );
      } else {
        map[card.key] = allUsers.filter((u) => u.category === card.category);
      }
    }
    return map;
  }, [allUsers]);

  // ── Summary stats ────────────────────────────────────────
  const stats = useMemo(() => {
    const demoTotal = usersByCategory.demos?.length || 0;
    const convertedTotal = usersByCategory.converted?.length || 0;
    return {
      totalDemos: demoTotal,
      totalConverted: convertedTotal,
      totalFreeSignups: usersByCategory.freeSignup?.length || 0,
      totalNoSignup: usersByCategory.noSignup?.length || 0,
      totalPaidWithoutDemo: usersByCategory.paidNoDemo?.length || 0,
      totalFreeWithoutDemo: usersByCategory.freeNoDemo?.length || 0,
      conversionRate:
        demoTotal > 0
          ? Math.round((convertedTotal / demoTotal) * 10000) / 100
          : 0,
    };
  }, [usersByCategory]);

  // ── Week-over-week stats (always from allUsersSource, no date-range filter) ──
  const weekStats = useMemo(() => {
    const thisWeek = getCalWeek(weekAOffset);
    const lastWeek = getCalWeek(weekBOffset);

    const countWeek = (bounds, cardKey) => {
      const meta = CARD_WEEK_META[cardKey];
      if (!meta) return 0;

      let subset;
      if (cardKey === "demos") {
        subset = allUsersSource.filter((u) =>
          ["converted", "freeSignup", "noSignup"].includes(u.category),
        );
        return subset.filter(
          (u) =>
            u.demoDate &&
            u.demoDate >= bounds.start &&
            u.demoDate <= bounds.end,
        ).length;
      }

      const card = CARDS.find((c) => c.key === cardKey);
      subset = card?.category
        ? allUsersSource.filter((u) => u.category === card.category)
        : allUsersSource;

      return subset.filter((u) => {
        const ts = u[meta.dateField];
        return ts && ts >= bounds.start && ts <= bounds.end;
      }).length;
    };

    const thisW = {};
    const lastW = {};
    for (const card of CARDS) {
      thisW[card.key] = countWeek(thisWeek, card.key);
      lastW[card.key] = countWeek(lastWeek, card.key);
    }

    const thisRate =
      thisW.demos > 0
        ? Math.round((thisW.converted / thisW.demos) * 10000) / 100
        : 0;
    const lastRate =
      lastW.demos > 0
        ? Math.round((lastW.converted / lastW.demos) * 10000) / 100
        : 0;
    const rateDiff = Math.round((thisRate - lastRate) * 100) / 100;

    return {
      this: { ...thisW, conversionRate: thisRate, bounds: thisWeek },
      last: { ...lastW, conversionRate: lastRate, bounds: lastWeek },
      rateDiff,
    };
  }, [allUsersSource, weekAOffset, weekBOffset]);

  // ── Data fetching ────────────────────────────────────────
  const fetchCache = useCallback(async () => {
    setError("");
    try {
      try {
        const currentYear = new Date().getFullYear();
        const yearDocRef = doc(
          firestoreProd,
          "admin-metrics",
          `lead-conversion-${currentYear}`,
        );
        const yearDocSnap = await getDoc(yearDocRef);
        if (yearDocSnap.exists()) {
          setRawData(yearDocSnap.data());
          setLoading(false);
          return;
        }
        // Fall back to legacy cache doc
        const cacheRef = doc(
          firestoreProd,
          "admin-metrics",
          "lead-conversion-cache",
        );
        const cacheSnap = await getDoc(cacheRef);
        if (cacheSnap.exists()) {
          setRawData(cacheSnap.data());
          setLoading(false);
          return;
        }
      } catch (_) {}

      const userId = getAuth()?.currentUser?.uid;
      if (!userId)
        throw new Error("Not authenticated. Please refresh and log in.");

      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");

      const baseUrls = isLocal ? [apiUrlDev, apiUrl] : [apiUrl];
      let lastError = "Failed to load lead conversion data";
      let loaded = false;

      for (const baseUrl of baseUrls) {
        try {
          const url = `${baseUrl}/admin/lead-conversion?userId=${userId}`;
          const controller = new AbortController();
          const timeout = setTimeout(
            () => controller.abort(),
            baseUrl.includes("localhost") ? 30000 : 15000,
          );
          const res = await fetch(url, { signal: controller.signal });
          clearTimeout(timeout);
          const json = await res.json();
          if (!res.ok || !json.success) {
            lastError = json.reason || `API error`;
            continue;
          }
          setRawData(json.data);
          loaded = true;
          break;
        } catch (innerErr) {
          lastError =
            innerErr?.name === "AbortError"
              ? `Request timed out`
              : innerErr?.message || `Could not reach server`;
        }
      }
      if (!loaded) throw new Error(lastError);
    } catch (err) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerRefresh = useCallback(async () => {
    const userId = getAuth()?.currentUser?.uid;
    if (!userId) return;
    setRefreshing(true);
    setError("");
    try {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");

      const baseUrls = isLocal ? [apiUrlDev, apiUrl] : [apiUrl];
      let lastError = "Refresh failed";
      let loaded = false;

      for (const baseUrl of baseUrls) {
        try {
          const url = `${baseUrl}/admin/lead-conversion/refresh?userId=${userId}`;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 180000);
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
          });
          clearTimeout(timeout);
          const json = await res.json();
          if (!res.ok || !json.success) {
            lastError = json.reason || `Refresh error`;
            continue;
          }
          setRawData(json.data);
          loaded = true;
          break;
        } catch (innerErr) {
          lastError =
            innerErr?.name === "AbortError"
              ? "Refresh timed out (this can take 1-2 min)"
              : innerErr?.message || `Could not reach server`;
        }
      }
      if (!loaded) throw new Error(lastError);
    } catch (err) {
      setError(err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCache().then(() => onReady && onReady());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCard = (key) =>
    setExpandedCard(expandedCard === key ? null : key);

  /* ─── RENDER ────────────────────────────────────────── */
  return (
    <div
      className="container-fluid LeadConversionPageContainer"
      style={{ paddingTop: 12, paddingBottom: 16 }}
    >
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="mb-1" style={{ fontWeight: 700 }}>
            Lead Conversion Dashboard
          </h2>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            Source: Calendly &amp; Firestore
            {rawData?.lastUpdated && (
              <span>
                {" "}
                · Updated {timeSince(rawData.lastUpdated)}{" "}
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  ({fmtDateTime(rawData.lastUpdated)})
                </span>
              </span>
            )}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {/* Week vs Week toggle + selectors */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn d-flex align-items-center gap-2"
              onClick={() => setShowComparison((v) => !v)}
              style={{
                background: showComparison
                  ? "linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)"
                  : "#f0f7ff",
                border: showComparison ? "none" : "1px solid #bfdbfe",
                padding: "8px 14px",
                borderRadius: 8,
                color: showComparison ? "#fff" : "#1d4ed8",
                fontWeight: 600,
                fontSize: 13,
                boxShadow: showComparison
                  ? "0 2px 12px rgba(29,78,216,0.3)"
                  : "none",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
            >
              <FaExchangeAlt />
              Week vs Week
            </button>

            {showComparison && (
              <>
                <select
                  value={weekAOffset}
                  onChange={(e) => setWeekAOffset(Number(e.target.value))}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #bfdbfe",
                    background: "#f0f7ff",
                    color: "#1d4ed8",
                    cursor: "pointer",
                    outline: "none",
                    maxWidth: 210,
                  }}
                >
                  {WEEK_OPTIONS.map((o) => (
                    <option key={o.offset} value={o.offset}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af" }}
                >
                  vs
                </span>
                <select
                  value={weekBOffset}
                  onChange={(e) => setWeekBOffset(Number(e.target.value))}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    color: "#374151",
                    cursor: "pointer",
                    outline: "none",
                    maxWidth: 210,
                  }}
                >
                  {WEEK_OPTIONS.map((o) => (
                    <option key={o.offset} value={o.offset}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          <button
            type="button"
            className="btn d-flex align-items-center gap-2"
            onClick={() => setShowWarmthPanel((v) => !v)}
            style={{
              background: showWarmthPanel
                ? "linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)"
                : "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
              border: "none",
              padding: "8px 20px",
              borderRadius: 8,
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              boxShadow: "0 2px 12px rgba(239,68,68,0.35)",
            }}
          >
            <FaFire />
            Potential Leads
          </button>
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={triggerRefresh}
            disabled={refreshing || loading}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              padding: "8px 20px",
              borderRadius: 8,
            }}
          >
            <FaSyncAlt className={refreshing ? "ci-spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh Data"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !rawData && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" />
            <p className="text-muted">Loading dashboard data...</p>
          </div>
        </div>
      )}

      {/* No cache yet */}
      {!loading && !rawData && !error && (
        <div className="text-center py-5">
          <FaUsers
            style={{ fontSize: 48, color: "#d1d5db", marginBottom: 16 }}
          />
          <h4 className="text-muted">No Data Yet</h4>
          <p className="text-muted mb-3">
            Click "Refresh Data" to build the cache.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={triggerRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Building..." : "Build Dashboard Data"}
          </button>
        </div>
      )}

      {/* Potential Leads Panel — shown above everything when toggled */}
      {showWarmthPanel && (
        <PotentialLeadsPanel onClose={() => setShowWarmthPanel(false)} />
      )}

      {/* Dashboard content */}
      {rawData && (
        <>
          {/* Conversion Rate + Date Filter row */}
          <div className="d-flex flex-wrap gap-3 align-items-stretch mb-3">
            {/* Conversion Rate Banner */}
            <div
              className="p-3 rounded-3 d-flex align-items-center gap-3 flex-grow-1 flex-wrap"
              style={{
                background: "#f8faff",
                border: "1px solid #bfdbfe",
                minWidth: 240,
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  backgroundColor: "#3b82f6",
                  color: "#fff",
                }}
              >
                <FaPercentage />
              </div>
              <div className="flex-grow-1">
                {!showComparison || !weekStats ? (
                  /* Normal mode */
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <span
                      className="fw-semibold"
                      style={{ fontSize: "1.3rem", color: "#111827" }}
                    >
                      {stats.conversionRate}% Conversion Rate
                    </span>
                    <span className="text-muted" style={{ fontSize: 13 }}>
                      ({stats.totalConverted} converted / {stats.totalDemos}{" "}
                      demos)
                    </span>
                  </div>
                ) : (
                  /* Comparison mode — hero side-by-side layout */
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    {/* Week A block */}
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#3b82f6",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          marginBottom: 2,
                        }}
                      >
                        {weekStats.this.bounds.label}
                      </div>
                      <div
                        style={{
                          fontSize: "1.6rem",
                          fontWeight: 800,
                          color: "#111827",
                          lineHeight: 1,
                        }}
                      >
                        {weekStats.this.conversionRate}%
                      </div>
                      <div
                        style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}
                      >
                        {weekStats.this.converted} converted ·{" "}
                        {weekStats.this.demos} demos
                      </div>
                    </div>

                    {/* Arrow + delta */}
                    <div className="d-flex flex-column align-items-center gap-1">
                      <span style={{ fontSize: 18, color: "#d1d5db" }}>→</span>
                      {weekStats.rateDiff !== 0 ? (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            background:
                              weekStats.rateDiff > 0 ? "#f0fdf4" : "#fef2f2",
                            color:
                              weekStats.rateDiff > 0 ? "#15803d" : "#b91c1c",
                            border: `1px solid ${weekStats.rateDiff > 0 ? "#bbf7d0" : "#fecaca"}`,
                            padding: "3px 10px",
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          {weekStats.rateDiff > 0 ? (
                            <FaArrowUp style={{ fontSize: 9 }} />
                          ) : (
                            <FaArrowDown style={{ fontSize: 9 }} />
                          )}
                          {weekStats.rateDiff > 0 ? "+" : ""}
                          {weekStats.rateDiff}pp
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>
                          — same
                        </span>
                      )}
                    </div>

                    {/* Week B block */}
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          marginBottom: 2,
                        }}
                      >
                        {weekStats.last.bounds.label}
                      </div>
                      <div
                        style={{
                          fontSize: "1.6rem",
                          fontWeight: 800,
                          color: "#9ca3af",
                          lineHeight: 1,
                        }}
                      >
                        {weekStats.last.conversionRate}%
                      </div>
                      <div
                        style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}
                      >
                        {weekStats.last.converted} converted ·{" "}
                        {weekStats.last.demos} demos
                      </div>
                    </div>

                    {/* Overall period rate (secondary) */}
                    <div
                      style={{
                        marginLeft: "auto",
                        paddingLeft: 16,
                        borderLeft: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                        }}
                      >
                        {selectedPreset} overall
                      </div>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: "#6b7280",
                        }}
                      >
                        {stats.conversionRate}%
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {stats.totalConverted} / {stats.totalDemos}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Range Filter */}
            <div
              className="p-3 rounded-3 d-flex align-items-center gap-2 flex-wrap"
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
              }}
            >
              <FaFilter style={{ color: "#9ca3af", fontSize: 13 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                Period:
              </span>
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    setSelectedPreset(p.label);
                    setShowCustomPicker(false);
                  }}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "4px 12px",
                    borderRadius: 999,
                    border: "1px solid",
                    cursor: "pointer",
                    background:
                      selectedPreset === p.label ? "#3b82f6" : "#f3f4f6",
                    color: selectedPreset === p.label ? "#fff" : "#374151",
                    borderColor:
                      selectedPreset === p.label ? "#3b82f6" : "#e5e7eb",
                    transition: "all 0.15s",
                  }}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setSelectedPreset("custom");
                  setShowCustomPicker(true);
                }}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "4px 12px",
                  borderRadius: 999,
                  border: "1px solid",
                  cursor: "pointer",
                  background:
                    selectedPreset === "custom" ? "#3b82f6" : "#f3f4f6",
                  color: selectedPreset === "custom" ? "#fff" : "#374151",
                  borderColor:
                    selectedPreset === "custom" ? "#3b82f6" : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <FaCalendarAlt style={{ fontSize: 11 }} />
                Custom
              </button>
              {showCustomPicker && (
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ fontSize: 12, width: 130 }}
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>→</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ fontSize: 12, width: 130 }}
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* 6 Metric Cards */}
          <div className="row g-3 mb-3">
            {CARDS.map((card) => {
              const users = usersByCategory[card.key] || [];
              const count = users.length;
              const isExpanded = expandedCard === card.key;
              const Icon = card.icon;

              return (
                <div
                  className="col-12 col-sm-6 col-xl-4 col-xxl-2"
                  key={card.key}
                >
                  <div
                    onClick={() => toggleCard(card.key)}
                    style={{
                      background: "#fff",
                      border: isExpanded
                        ? "2px solid #3b82f6"
                        : "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: "18px 16px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: isExpanded
                        ? "0 4px 20px rgba(59,130,246,0.15)"
                        : "0 2px 6px rgba(0,0,0,0.04)",
                      height: "100%",
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div>
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 mb-2"
                          style={{
                            width: 40,
                            height: 40,
                            background: card.gradient,
                            color: "#fff",
                            fontSize: 16,
                          }}
                        >
                          <Icon />
                        </div>
                        {showComparison && weekStats ? (
                          (() => {
                            const wA = weekStats.this[card.key] ?? 0;
                            const wB = weekStats.last[card.key] ?? 0;
                            const diff = wA - wB;
                            const meta = CARD_WEEK_META[card.key] || {
                              goodDir: 1,
                            };
                            const isGood =
                              diff === 0
                                ? null
                                : diff > 0
                                  ? meta.goodDir > 0
                                  : meta.goodDir < 0;
                            const pct =
                              wB > 0
                                ? Math.round(Math.abs(diff / wB) * 100)
                                : diff !== 0
                                  ? 100
                                  : 0;
                            return (
                              <>
                                {/* Week A count — primary */}
                                <div
                                  style={{
                                    fontSize: 30,
                                    fontWeight: 800,
                                    color: "#111827",
                                    lineHeight: 1,
                                  }}
                                >
                                  {wA}
                                </div>
                                {/* vs Week B */}
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: "#9ca3af",
                                    marginTop: 2,
                                  }}
                                >
                                  vs{" "}
                                  <strong style={{ color: "#6b7280" }}>
                                    {wB}
                                  </strong>
                                  <span style={{ marginLeft: 4, fontSize: 11 }}>
                                    prev week
                                  </span>
                                </div>
                                {/* Delta */}
                                {diff === 0 ? (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "#9ca3af",
                                      marginTop: 4,
                                      display: "block",
                                    }}
                                  >
                                    — no change
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      marginTop: 5,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 3,
                                      padding: "2px 8px",
                                      borderRadius: 999,
                                      background: isGood
                                        ? "#f0fdf4"
                                        : "#fef2f2",
                                      color: isGood ? "#15803d" : "#b91c1c",
                                      border: `1px solid ${isGood ? "#bbf7d0" : "#fecaca"}`,
                                    }}
                                  >
                                    {diff > 0 ? (
                                      <FaArrowUp style={{ fontSize: 8 }} />
                                    ) : (
                                      <FaArrowDown style={{ fontSize: 8 }} />
                                    )}
                                    {diff > 0 ? "+" : ""}
                                    {diff} ({pct}%)
                                  </span>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <div
                            style={{
                              fontSize: 30,
                              fontWeight: 800,
                              color: "#111827",
                              lineHeight: 1.1,
                            }}
                          >
                            {count}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#374151",
                            marginTop: 6,
                          }}
                        >
                          {card.label}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            marginTop: 2,
                          }}
                        >
                          {showComparison
                            ? weekStats?.this.bounds.label
                            : card.description}
                        </div>
                      </div>
                      <div style={{ color: "#9ca3af", marginTop: 4 }}>
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    </div>
                    {count > 0 && (
                      <div
                        className="mt-2"
                        style={{
                          fontSize: 11,
                          color: "#6b7280",
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: 6,
                        }}
                      >
                        {showComparison
                          ? `Click to view all ${count} users · ${selectedPreset}`
                          : `Click to ${isExpanded ? "hide" : "view"} ${count} users`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expanded drill-down */}
          {expandedCard &&
            (() => {
              const card = CARDS.find((c) => c.key === expandedCard);
              if (!card) return null;
              const cardUsers = usersByCategory[card.key] || [];
              const Icon = card.icon;
              return (
                <div
                  className="mb-3 p-3 rounded-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-3"
                      style={{
                        width: 32,
                        height: 32,
                        background: card.gradient,
                        color: "#fff",
                        fontSize: 14,
                      }}
                    >
                      <Icon />
                    </div>
                    <h5 className="mb-0 fw-bold">{card.label}</h5>
                    <span
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                        background: "#f3f4f6",
                        padding: "2px 10px",
                        borderRadius: 12,
                      }}
                    >
                      {cardUsers.length} users
                    </span>
                    <span
                      style={{ fontSize: 12, color: "#9ca3af", marginLeft: 4 }}
                    >
                      · filtered to{" "}
                      {selectedPreset === "custom"
                        ? `${customStart || "…"} → ${customEnd || "…"}`
                        : selectedPreset}
                    </span>
                  </div>
                  <DrillDownTable
                    key={expandedCard}
                    card={card}
                    users={cardUsers}
                  />
                </div>
              );
            })()}

          {/* Charts — always at the bottom */}
          <ChartsSection
            users={allUsers}
            granularity={getGranularity(selectedPreset, startTs)}
          />
        </>
      )}
    </div>
  );
};

export default LeadConversionPage;
